import { useEventCallback } from '@mui/material';
import { Form, Formik } from 'formik';
import { PracticeEditResidence } from 'practice/view-models/practice-edit-residence';
import { PmiButton } from 'shell/components/shared/button';
import { Fragment, memo, ReactElement, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import AccordionResidence from './accordion-residence';
import { PracticeAddress } from 'shell/models/practice-address';
import { DateTime } from 'luxon';
import { Message } from 'shell/components/shared/search-messages';
import * as yup from 'yup';
import { ResidenceID } from 'practice/constants/id-practice-edit-residence';
import { transformFamilyTable } from 'practice/utils/transform-family-table';
import { ObjectShape } from 'yup/lib/object';
import { TypeRegister } from 'practice/constants/type-register';
import { MilanMuncipality } from 'practice/constants/muncipality';
import { regexOnlyNumber } from 'shell/constants/regex';
import ModalWarning from 'shell/components/shared/modal-warning';
import {
  PracticeDraftBEStep,
  PracticeDraftStep,
  TypeInitiative,
} from 'practice/constants/practice-management-step';
import { TipoOccupazioneLegittima } from 'practice/constants/house-occupation';
// import PmiTooltipError from 'shell/components/shared/tooltip-error';

const validationSchema = (
  ForPracticeTemporary?: boolean,
  typeRegister?: string,
  fromPerson?: string
) => {
  let schema: ObjectShape = {};

  if (!ForPracticeTemporary) {
    if (typeRegister === TypeRegister.Emigration && fromPerson === TypeInitiative.idUfficio) {
      schema = {
        effectiveDate: yup
          .date()
          .typeError('Data non valida')
          .required('Campo richiesto')
          .nullable()
          .max(DateTime.now().plus({ day: 1 }), 'La data deve essere minore uguale ad oggi'),
        subscriptionNumber: yup
          .string()
          .nullable()
          .matches(regexOnlyNumber, 'Campo numerico')
          .when('citizenshipNumber', {
            is: 100,
            then: yup
              .string()
              .nullable()
              .matches(regexOnlyNumber, 'Campo numerico')
              .required('Campo obbligatorio'),
            otherwise: yup
              .string()
              .nullable()
              .matches(regexOnlyNumber, 'Campo numerico')
              .notRequired(),
          }),
        dateSubscriptionNumber: yup
          .date()
          .nullable()
          .typeError('Data non valida')
          .when('citizenshipNumber', {
            is: 100,
            then: yup.date().nullable().typeError('Data non valida').required('Campo obbligatorio'),
            otherwise: yup.date().nullable().typeError('Data non valida').notRequired(),
          }),
      };
    } else {
      schema = {
        effectiveDate: yup
          .date()
          .typeError('Data non valida')
          .required('Campo richiesto')
          .nullable()
          .max(DateTime.now().plus({ day: 1 }), 'La data deve essere minore uguale ad oggi'),
      };
    }
  } else {
    schema = {
      residence: yup
        .object()
        .nullable()
        .shape({
          italian: yup
            .object()
            .nullable()
            .shape({
              address: yup.object().nullable().required('Campo obbligatorio'),
            }),
        }),
    };
  }

  return yup.object({
    ...schema,
    citizenshipNumber: yup.number().nullable(),
  });
  // .test('', '', (values) => {
  //   const valuesCasted = values as unknown as PracticeEditResidence;
  //   if (!ForPracticeTemporary) {
  //     const existFamilyHead = valuesCasted.newResidenceFamily?.body?.some(
  //       (item) => item.relationshipValue === '1'
  //     );

  //     const allHasRelationship = valuesCasted.newResidenceFamily?.body?.every(
  //       (item) => item.relationshipValue
  //     );

  //     if (
  //       valuesCasted.typeResidence === ResidenceID.new &&
  //       (!valuesCasted.newResidenceFamily?.body?.length ||
  //         !existFamilyHead ||
  //         !allHasRelationship)
  //     ) {
  //       return new yup.ValidationError(
  //         'Inserire relazioni parentela',
  //         undefined,
  //         'newResidenceFamily'
  //       );
  //     }
  //   }
  //   return true;
  // });
};

interface StepResidenceProps {
  onBack: () => void;
  onNext: (step?: PracticeDraftStep) => void;
  idFamilyAnpr?: string;
  codeEnte?: string;
  yearId?: string;
  typeRegister?: string;
  progressive?: string;
  ForPracticeTemporary?: boolean;
  typePractice?: string;
  maxActiveStep: number;
  disabledAll?: boolean;
  iniziativa?: string;
  intestatarioIsAnpr?: boolean;
  fromPerson?: string;
}

const StepResidenceComponent = ({
  onBack,
  onNext,
  idFamilyAnpr,
  codeEnte,
  ForPracticeTemporary,
  yearId,
  typeRegister,
  progressive,
  typePractice,
  maxActiveStep,
  disabledAll,
  iniziativa,
  intestatarioIsAnpr,
  fromPerson,
}: StepResidenceProps): ReactElement => {
  const [warning, setWarning] = useState<{
    view: boolean;
    warning: Array<Message>;
    values: PracticeEditResidence;
    goToStep?: PracticeDraftStep;
    nameButton?: string;
    exitButton?: string;
  }>({
    view: false,
    warning: [],
    values: {},
  });

  const [indirizzo] = useHttpFetch<HttpResponse<PracticeAddress>, PracticeAddress>(
    `${__PRACTICE_API_URL__}/pratiche/indirizzi/italiano-o-estero/${codeEnte}-${yearId}-${typeRegister}-${progressive}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: (response) => {
        return response.data.payload;
      },
    }
  );

  const updateData = useHttpClient<HttpResponse<PracticeAddress>>(
    `${__PRACTICE_API_URL__}/pratiche/registrazione-nuovo-indirizzo`,
    {
      options: { method: 'POST' },
    }
  );

  // const saveData = useHttpClient(
  //   `${__PRACTICE_API_URL__}/visualizzazione-scelta-famiglia/inserisci`,
  //   {
  //     options: {
  //       method: 'POST',
  //     },
  //   }
  // );

  const onSave = useEventCallback((values: PracticeEditResidence) => {
    if (disabledAll) {
      onNext();
      return;
    }

    let idAnprFamigliaConvivenza;

    if (values.typeResidence === ResidenceID.family) {
      idAnprFamigliaConvivenza = values.tableFamiliar?.item?.idAnpr;
    }

    if (values.typeResidence === ResidenceID.domesticPartnership) {
      idAnprFamigliaConvivenza = (values.tableDomesticPartnership?.body || [])[0]?.idAnpr;
    }

    let idDbLocaleFamigliaConvivenza;

    if (values.typeResidence === ResidenceID.family) {
      idDbLocaleFamigliaConvivenza = values?.tableNewFamilySubjects?.idFamigliaConvivenza;
    }

    if (values.typeResidence === ResidenceID.domesticPartnership) {
      idDbLocaleFamigliaConvivenza = (values.tableDomesticPartnership?.body || [])[0]
        ?.IdCardMunicipality;
    }

    const data = {
      pratica: {
        key: {
          codiceEnte: codeEnte || undefined,
          tipoRegistro: typeRegister || undefined,
          idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
          progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
        },
      },
      casoPratica:
        typeRegister === TypeRegister.RegisterOfficeTemporary
          ? ResidenceID.new
          : values.typeResidence
          ? values.typeResidence
          : undefined,
      dataDecorrenza: values.effectiveDate
        ? values.effectiveDate.toFormat('yyyy-MM-dd')
        : undefined,
      stepBozza:
        maxActiveStep > PracticeDraftBEStep.Residence
          ? maxActiveStep
          : PracticeDraftBEStep.Residence,
      indirizzo:
        values.residence?.italian && !values.residence?.checkedResidence
          ? {
              tipoOccupanteLegittimo: values.residence?.italian?.occupantiLegittimi || undefined,
              flagConsensoOspitante: values.residence?.italian?.occupantiLegittimi
                ? values.residence?.italian?.assensoOspitante
                  ? '1'
                  : '0'
                : undefined,
              codSerieAe: values.residence?.italian?.serie?.id || undefined,
              // Proprietario
              codFiscaleProprietario:
                values.residence?.italian?.fiscalCodeProprietario || undefined,
              cognomeProprietario: values.residence?.italian?.surnameProprietario || undefined,
              nomeProprietario: values.residence?.italian?.nameProprietario || undefined,
              comuneProprietario:
                values.residence?.italian?.municipalityResidenceProprietario || undefined,
              descIndirizzoProprietario:
                values.residence?.italian?.addressProprietario || undefined,
              // Ospitante
              codFiscaleOspitante: values.residence?.italian?.fiscalCodeOspitante || undefined,
              cognomeOspitante: values.residence?.italian?.surnameOspitante || undefined,
              nomeOspitante: values.residence?.italian?.nameOspitante || undefined,
              dataNascitaOspitante:
                values.residence?.italian?.dataNascitaOspitante?.toFormat('yyyy-MM-dd') ||
                undefined,
              comuneResidenzaOspitante:
                values.residence?.italian?.municipalityResidenceOspitante?.code ||
                values.residence.italian.municipalityResidenceOspitanteERP ||
                undefined,
              indirizzoOspitante: values.residence?.italian?.addressOspitante || undefined,
              // ERP
              // codFiscaleErp: values.residence?.italian?.fiscalCodeERP || undefined,
              descrizioneEnteErp: values.residence?.italian?.enteERP || undefined,
              // Struttura
              codFiscaleEnteOspitante: values.residence?.italian?.fiscalCodeStruttura || undefined,
              nomeEnteOspitante: values.residence?.italian?.nameStruttura || undefined,
              indirizzoEnteOspitante: values.residence?.italian?.addressStruttura || undefined,
              comuneResidenzaEnteOspitante:
                values.residence?.italian?.municipalityResidenceStruttura?.code || undefined,
              descrizionePecEnte:
                values.residence?.italian?.pecEnteStruttura ||
                values.residence?.italian?.pecEnte ||
                undefined,
              flagControlloSiatel: values.residence.italian?.effettuatoControlloSiatel
                ? '1'
                : undefined,
              codVia:
                values.residence?.italian?.address?.code ||
                values.residence?.italian?.addressAuto?.code ||
                undefined,
              citta: values.residence?.italian.municipality?.description || undefined,
              provincia: values.residence?.italian.municipality?.province || undefined,
              cap: values.residence?.italian?.cap || undefined,
              nomeVia:
                values.residence?.italian?.address?.description ||
                values.residence?.italian?.addressAuto?.description ||
                undefined,
              numeroCivico: values.residence?.italian?.number || undefined,
              esponente: values.residence?.italian?.exponent || undefined,
              lettera: values.residence?.italian?.letter || undefined,
              sezione: values.residence?.italian?.section || undefined,
              foglio: values.residence?.italian?.sheet || undefined,
              particellaMappale: values.residence?.italian?.particle || undefined,
              subalterno: values.residence?.italian?.sub || undefined,
              categoriaCatastale: values.residence?.italian?.cat || undefined,
              flagIgnotaDimora: values.residence?.italian?.isHomeless ? '1' : '0',
              descrizioneSpecieToponimo:
                values.residence?.italian?.descrizioneSpecieToponimo || undefined,
              codTipoVia: values.residence?.italian?.codTipoVia || undefined,
              tipoOccupazione: values?.residence?.italian?.houseOccupationType || undefined,
              sedeAgenziaEntrate: values?.residence?.italian?.officeContract?.id || undefined,
              dataRegistrazioneAgenziaEntrate:
                values?.residence?.italian?.dateContract?.toFormat('yyyy-MM-dd') || undefined,
              numeroRegistrazioneAgenziaEntrate:
                values?.residence?.italian?.numberContract || undefined,
              notaOperatore: values?.residence?.italian?.note || undefined,
              flagAttualeResidenza:
                (values?.typeResidence === ResidenceID.new ||
                  values?.typeResidence === ResidenceID.allFamily) &&
                values.residence.flagAttualeResidenza
                  ? '1'
                  : '0',
            }
          : undefined,
      indirizzoEstero:
        values.residence?.foreign && values.residence?.checkedResidence
          ? {
              statoEstero: values.residence?.foreign.foreignState?.province || undefined,
              codComune:
                values?.typeResidence === ResidenceID.new &&
                values.residence?.foreign.municipality?.id
                  ? Number.parseInt(values.residence?.foreign.municipality?.id, 10)
                  : undefined,
              comune: values.residence?.foreign?.municipality?.description || undefined,
              codNazione:
                values?.typeResidence === ResidenceID.family &&
                values.residence?.foreign?.nationCode
                  ? Number(values.residence?.foreign?.nationCode)
                  : (values?.typeResidence === ResidenceID.new ||
                      values?.typeResidence === ResidenceID.allFamily) &&
                    values.residence?.foreign.foreignState?.id
                  ? values.residence?.foreign.foreignState?.id
                  : undefined,
              contea: values.residence?.foreign?.municipality?.province || undefined,
              cap: values.residence?.foreign?.cap || undefined,
              indirizzo: values.residence?.foreign?.address || undefined,
              civico: values.residence?.foreign?.civicNumber || undefined,
              codiceConsolato: values.residence?.foreign?.consulateCode?.id || undefined,
              codiceTerritorio:
                values.residence?.foreign?.territory?.id ||
                values.residence?.foreign.foreignState?.id ||
                undefined,
              dataIscrizioneAireConsolato: values.dateSubscriptionNumber
                ? values.dateSubscriptionNumber?.toFormat('yyyy-MM-dd')
                : undefined,
              nrIscrizioneAireConsolato: values.subscriptionNumber
                ? Number.parseInt(values.subscriptionNumber, 10)
                : undefined,
            }
          : undefined,
      proprietario:
        values.residence?.italian &&
        !values.residence?.checkedResidence &&
        (values.residence?.italian?.fiscalCodeProprietario ||
          values.residence?.italian?.surnameProprietario)
          ? {
              codFiscale: values.residence?.italian?.fiscalCodeProprietario || undefined,
              cognome: values.residence?.italian?.surnameProprietario || undefined,
              nome: values.residence?.italian?.nameProprietario || undefined,
              dataNascita:
                values.residence?.italian?.dataNascitaProprietario?.toFormat('yyyy-MM-dd') ||
                undefined,
              tipoProprietario: values.residence?.italian?.tipoProprietario || 'F',
            }
          : undefined,
      // casi famiglia e convivenza
      idAnprFamigliaConvivenza: idAnprFamigliaConvivenza || undefined,
      // casi famiglia e convivenza
      idDbLocaleFamigliaConvivenza: idDbLocaleFamigliaConvivenza || undefined,
      // caso convivenza
      nomeConvivenza:
        values.typeResidence === ResidenceID.domesticPartnership
          ? values.residence?.nomeConvivenza
          : undefined,
      // caso famiglia
      personeNuovaFamiglia:
        values.typeResidence === ResidenceID.family
          ? values.tableNewFamilySubjects?.body
              ?.filter((element) => element.hideRelationship)
              ?.map((item) => ({
                generalita: item.subject3002?.generalita,
                legameSoggetto: item.subject3002?.legameSoggetto
                  ? {
                      dataDecorrenza: item.subject3002?.legameSoggetto.dataDecorrenza || undefined,
                      codiceLegame: item.subject3002?.legameSoggetto.codiceLegame || undefined,
                      descrizioneCodiceLegame:
                        item.subject3002?.legameSoggetto.descrizioneCodiceLegame || undefined,
                      progressivoComponente:
                        item.subject3002?.legameSoggetto.progressivoComponente || undefined,
                      tipoLegame: item.subject3002?.legameSoggetto.tipoLegame || undefined,
                    }
                  : undefined,
                autoveicoli: item.subject3002?.autoveicoli,
              }))
          : undefined,
    };

    (updateData({ data }) as ResponsePromise<HttpResponse<PracticeAddress>>).then((response) => {
      const castedResponse = response?.data.payload;

      if (castedResponse?.erroriCatasto) {
        setWarning({
          view: true,
          warning: [
            {
              id: 1,
              value:
                'Attenzione: sono stati riscontrati errori sui dati catastali forniti - verificare sezione Eccezioni',
            },
          ],
          values,
          goToStep: castedResponse?.erroriCatasto ? PracticeDraftStep.ExceptionList : undefined,
          nameButton: castedResponse?.erroriCatasto ? 'Vai nelle eccezioni' : undefined,
          exitButton: castedResponse.erroriCatasto ? 'Ignora e vai avanti' : undefined,
        });
        return;
      }

      if ((castedResponse?.warning || []).length > 0) {
        const warningResult = (castedResponse?.warning || []).map((item, index) => {
          return {
            id: index,
            value: item || '',
          };
        });

        setWarning({
          view: true,
          warning: warningResult,
          values,
          goToStep: castedResponse?.erroriCatasto ? PracticeDraftStep.ExceptionList : undefined,
          nameButton: castedResponse?.erroriCatasto ? 'Vai nelle eccezioni' : undefined,
          exitButton: castedResponse?.erroriCatasto ? 'Ignora e vai avanti' : undefined,
        });

        return;
      }

      toast.success('Operazione eseguita con successo');

      onNext();
    });
  });

  const initialValues: PracticeEditResidence = useMemo(() => {
    if (indirizzo.isLoading || indirizzo.hasErrors || indirizzo.pristine || !indirizzo.data) {
      return {
        isFromData: false,
      };
    }

    const data: PracticeAddress = (indirizzo.data as PracticeAddress) || {};

    const dataItalian = data?.indirizzoVO || undefined;

    const dataForeign = data?.indirizzoEsteroVO || undefined;

    const convivenza = data.convivenza || undefined;

    const personeTrasferite = data?.personeTrasferite || [];

    const isElixAnpr = iniziativa === TypeInitiative.Anpr || iniziativa === TypeInitiative.ElixForm;

    const isFromData = isElixAnpr ? false : !!dataItalian || !!dataForeign;

    return {
      citizenshipNumber: data.cittadinanza,
      effectiveDate: data.dataDecorrenza ? DateTime.fromISO(data.dataDecorrenza || '') : undefined,
      typeResidence: data.casoPratica?.toString() || undefined,
      dateSubscriptionNumber: data.indirizzoEsteroVO?.dataProtocolloProvenienza
        ? DateTime.fromISO(data.indirizzoEsteroVO?.dataProtocolloProvenienza)
        : null,
      subscriptionNumber: data.indirizzoEsteroVO?.progressivoEnteProvenienza || '',
      residence: {
        checkedResidence:
          dataForeign || (typeRegister === TypeRegister.Residence && intestatarioIsAnpr)
            ? true
            : false, // estera
        isFromData,
        nomeConvivenza: convivenza?.nomeConvivenza || undefined,
        italian: dataItalian
          ? {
              municipality: {
                // id:

                description: dataItalian?.citta
                  ? dataItalian?.citta
                  : typeRegister === TypeRegister.RegisterOfficeTemporary
                  ? MilanMuncipality.description
                  : '',
                province: dataItalian?.provincia
                  ? dataItalian?.provincia
                  : typeRegister === TypeRegister.RegisterOfficeTemporary
                  ? MilanMuncipality.province
                  : '',
                provinceCode: dataItalian?.provincia
                  ? dataItalian?.provincia
                  : typeRegister === TypeRegister.RegisterOfficeTemporary
                  ? MilanMuncipality.provinceCode
                  : '',
              },
              assensoAggregazione: dataItalian.flagAssensoAggregazione === '1',
              aggregazioneLegittima: dataItalian.flagAggregazioneLegittima === '1',
              // assensoOspitante: dataItalian.flagAssensoOspitante === '1',
              cap: dataItalian?.cap || '',
              address: dataItalian?.nomeVia
                ? {
                    code: dataItalian.codVia,
                    description: dataItalian.nomeVia,
                  }
                : null,
              number: dataItalian.numeroCivico || '',
              exponent: dataItalian.esponente || '',
              // letter: dataItalian.lettera || '',
              effettuatoControlloSiatel: dataItalian.flagControlloSiatel === '1',
              section: dataItalian.sezione || '',
              sheet: dataItalian.foglio || '',
              particle: dataItalian.particellaMappale || '',
              sub: dataItalian.subalterno || '',
              cat: dataItalian.categoriaCatastale || '',
              isHomeless: dataItalian.flagIgnotaDimora === '1',
              codTipoVia: dataItalian.codTipoVia || '',
              occupantiLegittimi: dataItalian.tipoOccupanteLegittimo?.toString(),
              assensoOspitante: dataItalian.tipoOccupanteLegittimo
                ? dataItalian?.flagConsensoOspitante === '1'
                : false,
              // Proprietario
              tipoProprietario: (data.proprietario?.tipoProprietario as 'F' | 'G') || 'F',
              dataNascitaProprietario: data.proprietario?.dataNascita
                ? DateTime.fromISO(data.proprietario.dataNascita)
                : null,
              fiscalCodeProprietario: dataItalian?.codFiscaleProprietario || '',
              surnameProprietario: dataItalian?.cognomeProprietario || '',
              nameProprietario: dataItalian?.nomeProprietario || '',
              municipalityResidenceProprietario: dataItalian.comuneProprietario
                ? dataItalian.comuneProprietario
                : '',
              addressProprietario: dataItalian?.descIndirizzoProprietario || '',
              // Ospitante
              fiscalCodeOspitante: dataItalian?.codFiscaleOspitante || '',
              surnameOspitante: dataItalian?.cognomeOspitante || '',
              nameOspitante: dataItalian?.nomeOspitante || undefined,
              dataNascitaOspitante: dataItalian?.dataNascitaOspitante
                ? DateTime.fromISO(dataItalian.dataNascitaOspitante)
                : null,
              municipalityResidenceOspitante:
                dataItalian.tipoOccupanteLegittimo !==
                  TipoOccupazioneLegittima.OspiteIntestatarioERP &&
                dataItalian.comuneResidenzaOspitante
                  ? {
                      code: dataItalian.comuneResidenzaOspitante,
                      // description: dataItalian
                    }
                  : null,
              municipalityResidenceOspitanteERP:
                dataItalian.tipoOccupanteLegittimo !==
                  TipoOccupazioneLegittima.OspiteIntestatarioERP &&
                dataItalian.comuneResidenzaOspitante
                  ? dataItalian.comuneResidenzaOspitante
                  : '',
              addressOspitante: dataItalian?.indirizzoOspitante || undefined,
              // ERP
              fiscalCodeERP: dataItalian?.codFiscaleErp || undefined,
              enteERP: dataItalian?.descrizioneEnteErp,
              pecEnte: dataItalian?.descrizionePecEnte,
              // Struttura
              fiscalCodeStruttura: dataItalian?.codFiscaleEnteOspitante || undefined,
              nameStruttura: dataItalian?.nomeEnteOspitante || undefined,
              addressStruttura: dataItalian?.indirizzoEnteOspitante || undefined,
              municipalityResidenceStruttura: dataItalian.comuneResidenzaEnte
                ? {
                    code: dataItalian.comuneResidenzaEnte,
                    // description: dataItalian
                  }
                : null,
              pecEnteStruttura: dataItalian.descrizionePecEnte,
              addressAuto: dataItalian.codVia
                ? {
                    code: dataItalian.codVia,
                    description: dataItalian.nomeVia,
                  }
                : undefined,
              descrizioneSpecieToponimo: dataItalian.descrizioneSpecieToponimo || '',
              houseOccupationType: dataItalian.tipoOccupazione || '',
              serie: dataItalian.codSerieAe
                ? {
                    id: dataItalian.codSerieAe,
                    value: dataItalian.codSerieAe,
                  }
                : null,
              officeContract: dataItalian.sedeAgenziaEntrate
                ? {
                    id: dataItalian.sedeAgenziaEntrate,
                    value: dataItalian.sedeAgenziaEntrate,
                  }
                : null,
              dateContract: dataItalian.dataRegistrazioneAgenziaEntrate
                ? DateTime.fromISO(dataItalian.dataRegistrazioneAgenziaEntrate)
                : null,
              numberContract: dataItalian.numeroRegistrazioneAgenziaEntrate?.toString() || '',
              note: dataItalian.notaOperatore || '',
            }
          : TypeRegister.RegisterOfficeTemporary === typeRegister
          ? {
              municipality: MilanMuncipality,
            }
          : undefined,
        foreign: dataForeign
          ? {
              foreignState: {
                id: dataForeign.codNazione,
                value: dataForeign.statoEstero,
              },
              municipality: {
                description: dataForeign.comune || '',
                province: dataForeign.contea || '',
                id: dataForeign.codComune || '',
              },
              nationCode: dataForeign?.codNazione || '',
              cap: dataForeign.cap || '',
              address: dataForeign.indirizzo || '',
              civicNumber: dataForeign.civico || '',
              consulateCode: dataForeign.codiceConsolato ? { id: dataForeign.codiceConsolato } : '',
              territory: dataForeign.codiceTerritorio
                ? { id: dataForeign.codiceTerritorio }
                : undefined,
            }
          : undefined,
      },
      tableDomesticPartnership:
        convivenza && data.casoPratica?.toString() === ResidenceID.domesticPartnership
          ? {
              body: [
                {
                  IdCardMunicipality: convivenza.idDbLocaleConvivenza || '',
                  // codeIstat mancante
                  idAnpr: convivenza.idAnprConvivenza,
                  nameDomesticPartnership: convivenza.nomeConvivenza || '',
                  address: `${dataItalian?.nomeVia || ''}, ${dataItalian?.numeroCivico || ''} ${
                    dataItalian?.esponente || dataItalian?.lettera
                      ? `/${dataItalian?.esponente || ''}${dataItalian?.lettera || ''}`
                      : ''
                  }`,
                  isActive: true,
                },
              ],
            }
          : undefined,
      tableNewFamilySubjects:
        personeTrasferite.length > 0 && data.casoPratica?.toString() === ResidenceID.family
          ? {
              body: (transformFamilyTable(personeTrasferite) || [])?.map((item) => ({
                ...item,
                relationshipValue: item.relationship || undefined,
              })),
              residence: dataItalian
                ? {
                    municipality: {
                      // id:
                      description: dataItalian?.citta || '',
                      province: dataItalian?.provincia || '',
                    },
                    cap: dataItalian?.cap || '',
                    address: dataItalian?.nomeVia
                      ? {
                          code: dataItalian.codVia,
                          description: dataItalian.nomeVia,
                        }
                      : null,
                    number: dataItalian.numeroCivico || '',
                    exponent: dataItalian.esponente || '',
                    // letter: dataItalian.lettera || '',
                    section: dataItalian.sezione || '',
                    sheet: dataItalian.foglio || '',
                    particle: dataItalian.particellaMappale || '',
                    sub: dataItalian.subalterno || '',
                    cat: dataItalian.categoriaCatastale || '',
                    isHomeless: dataItalian.flagIgnotaDimora === '1',
                    codTipoVia: dataItalian.codTipoVia || '',
                    addressAuto: dataItalian.codVia
                      ? {
                          code: dataItalian.codVia,
                          description: dataItalian.nomeVia,
                        }
                      : undefined,
                    descrizioneSpecieToponimo: dataItalian.descrizioneSpecieToponimo || '',
                    houseOccupationType: dataItalian.tipoOccupazione || '',
                    officeContract: dataItalian.sedeAgenziaEntrate
                      ? {
                          id: dataItalian.sedeAgenziaEntrate,
                          value: dataItalian.sedeAgenziaEntrate,
                        }
                      : null,
                    dateContract: dataItalian.dataRegistrazioneAgenziaEntrate
                      ? DateTime.fromISO(dataItalian.dataRegistrazioneAgenziaEntrate)
                      : null,
                    numberContract: dataItalian.numeroRegistrazioneAgenziaEntrate?.toString() || '',
                    note: dataItalian.notaOperatore || '',
                  }
                : dataForeign
                ? {
                    foreignState: {
                      id: dataForeign.statoEstero,
                      value: dataForeign.statoEstero,
                    },
                    municipality: {
                      description: dataForeign.comune || '',
                      province: dataForeign.contea || '',
                      id: dataForeign.codComune || '',
                    },
                    nationCode: dataForeign?.codNazione || '',
                    cap: dataForeign.cap || '',
                    address: dataForeign.indirizzo || '',
                    civicNumber: dataForeign.civico || '',
                    consulateCode: dataForeign.codiceConsolato
                      ? { id: dataForeign.codiceConsolato }
                      : '',
                    territory: dataForeign.codiceTerritorio
                      ? { id: dataForeign.codiceTerritorio }
                      : undefined,
                  }
                : undefined,
            }
          : undefined,
      tableFamiliar:
        personeTrasferite.length > 0 && data.casoPratica?.toString() === ResidenceID.family
          ? {
              body: (transformFamilyTable(personeTrasferite) || [])?.map((item) => ({
                ...item,
                relationshipValue: item.relationship || undefined,
              })),
              item: (transformFamilyTable(personeTrasferite) || [])[0],
            }
          : undefined,
    } as PracticeEditResidence;
  }, [indirizzo]);

  const onCloseWarning = useEventCallback(() => {
    const { goToStep } = warning;
    setWarning({ view: false, warning: [], values: {} });

    onNext(goToStep);
  });

  const isLoading = indirizzo.isLoading || indirizzo.hasErrors || indirizzo.pristine;

  return (
    <Fragment>
      {isLoading ? undefined : (
        <Formik
          initialValues={initialValues}
          onSubmit={disabledAll ? () => onNext() : onSave}
          validationSchema={validationSchema(ForPracticeTemporary, typeRegister, fromPerson)}
        >
          <Form>
            <AccordionResidence
              ForPracticeTemporary={ForPracticeTemporary}
              idFamilyAnpr={idFamilyAnpr}
              codeEnte={codeEnte}
              yearId={yearId}
              typeRegister={typeRegister}
              intestatarioIsAnpr={intestatarioIsAnpr}
              progressive={progressive}
              typePractice={typePractice}
              fromPerson={fromPerson}
              disabled={disabledAll}
              iniziativa={iniziativa}
            />
            <ModalWarning
              open={warning.view as boolean}
              onClose={onCloseWarning}
              message={(warning.warning as Array<Message>)?.map((el) => el.value)?.join('')}
              withAnnulment
              onAnnulment={() => {
                if (warning.exitButton) {
                  onNext();
                }
                setWarning({ view: false, warning: [], values: {} });
              }}
              nameButton={warning.goToStep ? warning.nameButton : 'Prosegui'}
              exitButton={warning.exitButton}
            />
            <div className="pmi-pt-3 pmi-d-flex pmi-justify-content-center">
              <div className="pmi-w-25 pmi-w-lg-15">
                <PmiButton variant="outlined" fullWidth color="cdmBrand" onClick={onBack}>
                  Indietro
                </PmiButton>
              </div>
              <div className="pmi-pl-2 pmi-w-25 pmi-w-lg-15">
                <PmiButton variant="outlined" fullWidth color="cdmBrandThird" type="submit">
                  Avanti
                </PmiButton>
              </div>
            </div>
          </Form>
        </Formik>
      )}
    </Fragment>
  );
};

StepResidenceComponent.displayName = 'step component Residence';

const StepResidence = memo(StepResidenceComponent) as typeof StepResidenceComponent;

export default StepResidence;
